import React from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { showBetaBadge, betaBadgeLabel, betaBadgeDescription } from '@/config/release';

export interface BetaBadgeProps {
  /** Header bar: compact. Hero: slightly larger for page titles. */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Prominent beta ribbon; hidden entirely when `VITE_SHOW_BETA_BADGE` is not true.
 */
export const BetaBadge: React.FC<BetaBadgeProps> = ({ size = 'sm', className }) => {
  if (!showBetaBadge) return null;

  const isMd = size === 'md';

  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center gap-1 rounded-full font-bold uppercase tracking-widest text-white shadow-lg ring-1 ring-inset ring-white/35',
        'bg-gradient-to-r from-violet-600 via-fuchsia-500 to-indigo-600',
        'shadow-fuchsia-500/25 transition-transform duration-200 hover:scale-[1.03] motion-reduce:transform-none',
        isMd ? 'px-3 py-1 text-xs sm:text-sm tracking-[0.18em]' : 'px-2 py-0.5 text-[0.62rem] sm:px-2.5 sm:py-0.5 sm:text-[0.65rem] sm:tracking-[0.2em]',
        className
      )}
      role="status"
      title={betaBadgeDescription}
      aria-label={`${betaBadgeLabel}: ${betaBadgeDescription}`}
    >
      <Sparkles
        className={cn(
          'relative shrink-0 opacity-95 motion-safe:animate-pulse motion-reduce:animate-none',
          isMd ? 'h-3.5 w-3.5 sm:h-4 sm:w-4' : 'h-2.5 w-2.5 sm:h-3 sm:w-3'
        )}
        aria-hidden
        strokeWidth={2.25}
      />
      <span className="relative">{betaBadgeLabel}</span>
    </span>
  );
};
